import { useEffect, useState } from "react";
import { useDataAdapter } from "@shared-core/data/DataAdapterContext";
import type { StockPickItem } from "@shared-core/data/api-types";
import { ApiError } from "@shared-core/data/api/errors";
import {
  formatCount,
  formatDateTimeOrDash,
} from "@shared-core/domain/formatters";
import { ListPagination } from "../../app/components/patterns/ListPagination";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../app/components/primitives/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../app/components/primitives/table";

const PICKS_PAGE_SIZE = 10;

/**
 * The item's open picks: stock claimed for a despatch, counted as pending
 * despatch until it is booked out. Read-only here: picks are made by orders
 * (a from-stock line at Release) and booked out when the line is despatched.
 */
export function StockPicksCard({
  stockItemId,
  reloadKey,
}: {
  stockItemId: string;
  /** Changes whenever the page re-reads the item, so the picks re-read too. */
  reloadKey: number;
}) {
  const dataAdapter = useDataAdapter();
  const [picks, setPicks] = useState<StockPickItem[]>([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [status, setStatus] = useState<"loading" | "ready" | "error">(
    "loading",
  );

  useEffect(() => {
    let cancelled = false;
    dataAdapter
      .listStockPicks(stockItemId, {
        state: "open",
        page,
        pageSize: PICKS_PAGE_SIZE,
      })
      .then((result) => {
        if (cancelled) return;
        setPicks(result.items);
        setTotalPages(result.totalPages);
        setStatus("ready");
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        console.error("stock.picks.load_failed", {
          status: error instanceof ApiError ? error.status : undefined,
        });
        setStatus("error");
      });
    return () => {
      cancelled = true;
    };
  }, [dataAdapter, stockItemId, page, reloadKey]);

  return (
    <Card>
      <CardHeader>
        <CardTitle>Pending despatch</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {status === "loading" ? (
          <p className="text-sm text-muted-foreground">Loading picks…</p>
        ) : status === "error" ? (
          <p role="alert" className="text-sm text-red-700 dark:text-red-300">
            Couldn't load the picks. Please try again.
          </p>
        ) : picks.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            Nothing is picked for despatch. Order lines supplied from stock pick
            it when they're released.
          </p>
        ) : (
          <>
            <p className="text-sm text-muted-foreground">
              Stock claimed for despatch. It is booked out, oldest batch first,
              when the line is despatched.
            </p>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Picked for</TableHead>
                    <TableHead>Warehouse</TableHead>
                    <TableHead className="text-right">Quantity</TableHead>
                    <TableHead>Picked</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {picks.map((pick) => (
                    <TableRow key={pick.id}>
                      <TableCell className="font-mono text-xs">
                        {pick.picked_for_ref}
                      </TableCell>
                      <TableCell className="whitespace-nowrap">
                        {pick.location_name ?? pick.location_id}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {formatCount(pick.quantity)}
                      </TableCell>
                      <TableCell className="whitespace-nowrap">
                        {formatDateTimeOrDash(pick.created_at)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </>
        )}
        <ListPagination
          page={page}
          totalPages={totalPages}
          onPageChange={setPage}
        />
      </CardContent>
    </Card>
  );
}
